/* eslint-disable react/no-unknown-property */
// `test-id` is the attribute this repo's e2e selectors use. On a design-system
// component it is a declared prop; on the native `<input>` below it is a plain
// attribute React passes through, which this rule does not know about — the
// same reason `pro-studio`'s `use-file-input.tsx` carries this directive.
import {
  Button,
  Icon,
  IconButton,
  Modal,
  ModalProps,
  Spacer,
  Stack,
  TextBody,
  TextTitle,
  Textarea,
} from '@pypestream/design-system';
import {
  buildSupportSearchQuery,
  submitUserBugReport,
  type RejectedScreenshot,
  type ScreenshotRejectionReason,
} from '@pypestream/utils';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
} from 'react';

import {
  canSubmitBugReport,
  extractPastedScreenshots,
  mergeBugReportScreenshots,
} from './report-bug-modal.utils';

type SubmitStatus = 'editing' | 'sending' | 'sent' | 'failed';

/**
 * Every user-facing string the form needs, already translated.
 *
 * Handed in rather than looked up, which is the shape `AppErrorBoundary` in
 * this package already uses (`labels`). It is what keeps the package free of
 * `@pypestream/translations`, and it is not merely a dependency preference:
 * each app initialises i18next with ONE scope and loads only that scope's
 * namespaces (`getScopeNameSpaces` in `packages/translations/src/i18n.ts`), so
 * a key hardcoded here would resolve in whichever app owns that scope and
 * render as a raw key in the other.
 *
 * The interpolated ones are functions because word order is not universal — a
 * caller must be able to place the value inside its own sentence.
 */
export interface ReportBugModalLabels {
  title: string;
  subtitle: string;
  messagePlaceholder: string;
  /** Already carries the attachment limit; the caller interpolates it. */
  screenshotsHint: string;
  attach: string;
  cancel: string;
  submit: string;
  sending: string;
  done: string;
  sendFailed: string;
  successTitle: string;
  successBody: string;
  copied: string;
  copyAria: (errorId: string) => string;
  screenshotAlt: (fileName: string) => string;
  removeScreenshot: (fileName: string) => string;
  /** Names the message box. Its placeholder disappears at the first keystroke. */
  messageLabel: string;
  /** Why Send is not available yet — shown, not hidden in a disabled tooltip. */
  submitHint: string;
  /** Said out loud when an attachment lands, so a paste is not silent. */
  screenshotAttached: (fileName: string) => string;
  /** Said out loud when one is dropped, for the same reason. */
  screenshotRemoved: (fileName: string) => string;
  /** Shown while files are being dragged over the page, as the drop target. */
  dropHint: string;
  /** The clipboard can refuse; saying nothing lets the user paste stale text. */
  copyFailed: string;
  /** Why a file was turned away — one message per reason the picker reports. */
  rejected: Record<ScreenshotRejectionReason, (fileName: string) => string>;
}

export interface ReportBugModalProps {
  modalRef: React.RefObject<ModalProps> | null;
  onClose?: () => void;
  /**
   * Whether the modal is on screen. The paste listener below is bound to the
   * DOCUMENT, so it must not be live while the form is closed — otherwise a
   * Cmd+V anywhere in the app would quietly attach a screenshot to a form
   * nobody opened.
   */
  isOpen: boolean;
  /** Which app the report came from, matching the tag `reportError` sets. */
  app: string;
  /** Who to come back to. Both halves are optional — a session may know neither. */
  reporter?: { email?: string; name?: string };
  labels: ReportBugModalLabels;
}

export const ReportBugModal: FC<ReportBugModalProps> = ({
  modalRef,
  onClose,
  isOpen,
  app,
  reporter,
  labels,
}) => {
  const [message, setMessage] = useState('');
  const [screenshots, setScreenshots] = useState<File[]>([]);
  const [rejected, setRejected] = useState<RejectedScreenshot[]>([]);
  const [status, setStatus] = useState<SubmitStatus>('editing');
  const [errorId, setErrorId] = useState('');
  const [copied, setCopied] = useState(false);
  // A callback ref rather than `useRef`, because the node this listener belongs
  // to comes and goes: the `<input>` lives inside the `status !== 'sent'`
  // branch, so sending a report unmounts it and Done mounts a BRAND NEW one.
  // An effect keyed on anything else keeps its listener on the discarded node
  // and never binds the replacement — measured before the fix: after sending
  // one report, Attach opened the picker and choosing a file did nothing at
  // all, while Cmd+V still worked, because the paste listener is on `document`.
  const [fileInput, setFileInput] = useState<HTMLInputElement | null>(null);
  // What the always-mounted `role="status"` region says. Rejections already had
  // a voice here; acceptance, removal and a refused copy did not, so the form
  // announced only its failures.
  const [notice, setNotice] = useState('');
  // Whether a file is currently being dragged anywhere over the page. A COUNT
  // rather than a boolean while it is tracked, because `dragleave` fires every
  // time the pointer crosses into a CHILD element, so a boolean flickers off
  // over any nested node.
  const [dragDepth, setDragDepth] = useState(0);
  // Pastes made so far, NOT files attached: reusing the attachment count
  // repeats a name as soon as one is removed.
  const pastedCount = useRef(0);
  // The success screen replaces the controls that held focus, so focus has to
  // be given somewhere deliberately — otherwise it falls to <body> and the user
  // hears nothing at all.
  const successHeading = useRef<HTMLDivElement>(null);
  const attachmentsRegion = useRef<HTMLDivElement>(null);
  // Focus lands here when a thumbnail's remove button is unmounted under the
  // caret. It has to be a plain element: no design-system component sets
  // `delegatesFocus`, so `.focus()` on one of their hosts does nothing at all
  // (checked across the whole package, not assumed).

  // An object URL holds its blob alive until it is revoked, and a report can
  // carry 20MB of them. Revoking on the next change — rather than only on
  // close — is what keeps a session of repeated picks from accumulating them.
  const previews = useMemo(
    () =>
      screenshots.map((file) => ({
        file,
        url: URL.createObjectURL(file),
      })),
    [screenshots]
  );

  useEffect(
    () => () => previews.forEach(({ url }) => URL.revokeObjectURL(url)),
    [previews]
  );

  const addFiles = useCallback(
    (picked: File[]) => {
      if (!picked.length) return;

      setScreenshots((current) => {
        const { accepted, rejected: refused } = mergeBugReportScreenshots(
          current,
          picked
        );

        setRejected(refused);

        const landed = accepted.filter((file) => !current.includes(file));

        // A rejection was already announced; an acceptance was not, so a paste
        // that WORKED was indistinguishable from one the form ignored — the
        // gesture this listener exists for produces no other feedback.
        setNotice(
          landed.length ? labels.screenshotAttached(landed[0].name) : ''
        );

        return accepted;
      });
    },
    [labels]
  );

  /**
   * Cmd+Shift+4 then Cmd+V — the way a screenshot is actually taken here.
   *
   * Bound to the `document`, for two reasons measured on 2026-09-20. A React
   * `onPaste` never fires at all inside this modal: `ps-modal` re-parents its
   * subtree into `ps-portal-destination`, outside the container React binds its
   * listeners to. And a listener on the form itself would only catch a paste
   * while something inside it holds focus, so a user who opened the form and
   * pressed Cmd+V straight away would see nothing happen.
   */
  useEffect(() => {
    if (!isOpen || status === 'sent') return undefined;

    const handlePaste = (event: globalThis.ClipboardEvent) => {
      const pasted = extractPastedScreenshots(
        event.clipboardData,
        pastedCount.current
      );

      if (!pasted.length) return;

      pastedCount.current += pasted.length;

      // Only once an image was actually found: a plain-text paste into the
      // message box has to keep working.
      event.preventDefault();
      addFiles(pasted);
    };

    document.addEventListener('paste', handlePaste);

    return () => document.removeEventListener('paste', handlePaste);
  }, [addFiles, isOpen, screenshots.length, status]);

  /**
   * The picker's `change` is bound to the DOM node, not through React's
   * `onChange`, and that is load-bearing rather than a style choice: `ps-modal`
   * re-parents its subtree into `ps-portal-destination`, a sibling of `#root`
   * under `<body>`, while React 18 binds its listeners to the container given
   * to `createRoot`. A native `<input>` rendered inside a modal therefore
   * receives no synthetic events at all — the design-system components around
   * it keep working only because their `@lit/react` wrappers bind real ones.
   */
  useEffect(() => {
    const node = fileInput;

    if (!node) return undefined;

    const handleChange = () => {
      addFiles(Array.from(node.files ?? []));
      // Without this, picking the same file twice in a row fires no `change`,
      // so removing a screenshot and choosing it again would do nothing. The
      // browser's re-selection suppression is not reproducible in jsdom, but
      // the ASSIGNMENT is: `report-bug-modal.test.tsx` spies on the `value`
      // setter, which is what makes deleting this line go red.
      node.value = '';
    };

    node.addEventListener('change', handleChange);

    return () => node.removeEventListener('change', handleChange);
  }, [addFiles, fileInput]);

  /**
   * Drag a screenshot from the desktop onto the form.
   *
   * Bound to the `document` for the two reasons the paste listener above is,
   * plus a third that is specific to dropping. (a) `ps-modal` re-parents its
   * subtree into `ps-portal-destination`, so a React `onDrop` on this markup
   * never fires. (b) A listener on the form alone would only cover the form's
   * own box — and a drop that MISSES it is not harmless: with no handler, the
   * browser's default action is to NAVIGATE to the dropped file, which throws
   * away everything the user has typed and attached. Measured on 2026-09-20,
   * before this existed: a synthesized drop on the open modal left
   * `defaultPrevented` false in both `dragover` and `drop`, and nothing in the
   * app or in `ps-modal` called `preventDefault` on either. So while the form
   * is open, the whole document is the drop target.
   *
   * `dragover` MUST preventDefault as well, and not only as a courtesy: the
   * drop event is not delivered at all unless the dragover before it was
   * cancelled (HTML spec, "drag-and-drop processing model").
   *
   * Scoped to the editing screen: once a report is sent there is nothing to
   * attach to, and the guard is what puts the page's default behaviour back
   * rather than swallowing drops for the rest of the session.
   */
  useEffect(() => {
    if (!isOpen || status === 'sent') return undefined;

    const carriesFiles = (event: globalThis.DragEvent): boolean =>
      Array.from(event.dataTransfer?.types ?? []).includes('Files');

    const handleEnter = (event: globalThis.DragEvent) => {
      if (!carriesFiles(event)) return;
      event.preventDefault();
      setDragDepth((depth) => depth + 1);
    };

    const handleOver = (event: globalThis.DragEvent) => {
      if (!carriesFiles(event)) return;
      event.preventDefault();
      // Tells the pointer this is a copy, not a move — without it the cursor
      // keeps whatever the OS guessed.
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
    };

    const handleLeave = (event: globalThis.DragEvent) => {
      if (!carriesFiles(event)) return;
      setDragDepth((depth) => Math.max(0, depth - 1));
    };

    const handleDrop = (event: globalThis.DragEvent) => {
      if (!carriesFiles(event)) return;
      // Cancelled even while sending: the alternative is not "nothing
      // happens", it is the browser navigating away from a report in flight.
      event.preventDefault();
      setDragDepth(0);

      if (status === 'sending') return;

      addFiles(Array.from(event.dataTransfer?.files ?? []));
    };

    const handleEnd = () => setDragDepth(0);

    document.addEventListener('dragenter', handleEnter);
    document.addEventListener('dragover', handleOver);
    document.addEventListener('dragleave', handleLeave);
    document.addEventListener('drop', handleDrop);
    document.addEventListener('dragend', handleEnd);

    return () => {
      document.removeEventListener('dragenter', handleEnter);
      document.removeEventListener('dragover', handleOver);
      document.removeEventListener('dragleave', handleLeave);
      document.removeEventListener('drop', handleDrop);
      document.removeEventListener('dragend', handleEnd);
      // A drag that was in progress when the form closed must not leave the
      // hint showing the next time it opens.
      setDragDepth(0);
    };
  }, [addFiles, isOpen, status]);

  /**
   * Copies the SEARCH, not the bare id.
   *
   * The id alone finds nothing on Sentry's User Feedback page — measured in
   * the real UI on 2026-09-20, it searches by tag — so whoever receives it
   * would have to know to type a prefix. The button shows the id, because that
   * is what a person reads out; the clipboard gets the query that works.
   *
   * `writeText` rejects when the document is not focused or the permission is
   * refused; the id stays on screen either way, so a failure simply leaves the
   * confirmation unshown rather than breaking the screen.
   */
  const copyErrorId = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(buildSupportSearchQuery(errorId));
      setCopied(true);
      setNotice('');
    } catch {
      setCopied(false);
      // Silence here is worse than the failure: the user believes the id is on
      // the clipboard and pastes whatever was there before.
      setNotice(labels.copyFailed);
    }
  }, [errorId, labels]);

  useEffect(() => {
    if (status === 'sent') successHeading.current?.focus();
  }, [status]);

  // The confirmation is temporary, and the timer has to die with the component
  // — otherwise it sets state on an unmounted form.
  useEffect(() => {
    if (!copied) return undefined;

    const timer = window.setTimeout(() => setCopied(false), 2000);

    return () => window.clearTimeout(timer);
  }, [copied]);

  const removeScreenshot = useCallback(
    (file: File) => {
      setScreenshots((current) => current.filter((item) => item !== file));
      setRejected([]);
      setNotice(labels.screenshotRemoved(file.name));
      // The button that had focus is being unmounted, and `focus-trap` only
      // rescues focus when the focused node ITSELF is removed — here it is a
      // child of the removed wrapper, so focus would fall to <body> and the
      // next Tab would restart from the top of the page.
      attachmentsRegion.current?.focus();
    },
    [labels]
  );

  const resetAndClose = useCallback(() => {
    setMessage('');
    setScreenshots([]);
    setRejected([]);
    setStatus('editing');
    setErrorId('');
    setCopied(false);
    setNotice('');
    pastedCount.current = 0;
    onClose?.();
  }, [onClose]);

  const handleSubmit = useCallback(async () => {
    setStatus('sending');
    // The form's own status line does not belong to the screen that replaces
    // it. Without this the success screen inherits whatever the form said
    // last — typically "<name> attached" — which reads as a claim about the
    // report that was just sent. The success region is reused for the copy
    // button's own failure, so it has to start empty rather than not exist.
    setNotice('');

    try {
      const { errorId: id } = await submitUserBugReport({
        message,
        app,
        screenshots,
        url: window.location.href,
        user: reporter,
      });

      setErrorId(id);
      setStatus('sent');
    } catch {
      // The user keeps everything they typed, so Try again costs them nothing.
      setStatus('failed');
    }
  }, [app, message, reporter, screenshots]);

  const canSubmit =
    status !== 'sending' && canSubmitBugReport({ message, screenshots });
  // `dragDepth` counts nested enter/leave pairs; what the UI needs is the
  // single question "is something being dragged right now".
  const isDropTarget = dragDepth > 0 && status !== 'sending';

  return (
    <Modal
      test-id="ps-report-bug-modal"
      ref={modalRef}
      onClose={resetAndClose}
      size="medium"
    >
      {status === 'sent' ? (
        <>
          {/* Focused and announced. Submitting unmounts the whole form, and
              the button that had focus goes with it — `focus-trap` only
              rescues focus when the focused node ITSELF is removed, so without
              this the caret falls to <body> and a screen-reader user is told
              nothing about a report that was just sent. */}
          <div ref={successHeading} tabIndex={-1} role="status">
            <TextTitle size="small">{labels.successTitle}</TextTitle>
          </div>
          <Spacer size="small" />
          <TextBody size="small">{labels.successBody}</TextBody>
          <Spacer size="2xsmall" />
          <Button
            test-id="ps-report-bug-modal-error-id"
            size="small"
            variant="ghost"
            label={labels.copyAria(errorId)}
            onClick={copyErrorId}
          >
            <Icon name={copied ? 'check' : 'copy'} slot="prefix" />
            {errorId}
          </Button>
          {/*
            Always mounted, so the confirmation is announced when it appears
            rather than arriving with its own container (WCAG 4.1.3) — and it
            holds its line whether or not the text is there, so pressing the
            button cannot resize the dialog under the pointer.
          */}
          <div role="status" aria-live="polite" style={{ minHeight: '1.5em' }}>
            {copied && <TextBody size="small">{labels.copied}</TextBody>}
            {/* The copy button lives on THIS screen, so its failure has to be
                announced here — the form's own status region is unmounted by
                now. Saying nothing leaves the user pasting whatever was on the
                clipboard before. */}
            {!copied && notice && <TextBody size="small">{notice}</TextBody>}
          </div>
          <Spacer size="small" />
          <Stack justifyContent="end" gutter="small">
            <Button
              test-id="ps-report-bug-modal-done-btn"
              size="large"
              onClick={resetAndClose}
            >
              {labels.done}
            </Button>
          </Stack>
        </>
      ) : (
        <div>
          <TextTitle size="small">{labels.title}</TextTitle>
          <Spacer size="small" />
          <TextBody size="small">{labels.subtitle}</TextBody>
          <Spacer size="small" />
          <Textarea
            // The placeholder is gone at the first keystroke, so it cannot be
            // the field's name. `label` reaches the real <textarea> inside the
            // component's shadow root; an `aria-label` here would not.
            label={labels.messageLabel}
            test-id="ps-report-bug-modal-message"
            variant="outlined"
            rows={5}
            value={message}
            disabled={status === 'sending'}
            placeholder={labels.messagePlaceholder}
            onInput={(event) =>
              setMessage((event.target as HTMLTextAreaElement).value)
            }
          />
          <Spacer size="small" />
          {/*
            The hint swaps to the drop target's own wording while a file is
            over the page, so the form says what will happen if the pointer is
            released. It is the SAME node rather than an extra one that appears
            on drag: a region that arrives together with its text is not
            reliably announced (WCAG 4.1.3), and swapping the text inside a
            node that is already there is what an assistive technology reads.
          */}
          <TextBody size="small" test-id="ps-report-bug-modal-attach-hint">
            {isDropTarget ? labels.dropHint : labels.screenshotsHint}
          </TextBody>
          <Spacer size="2xsmall" />
          <input
            test-id="ps-report-bug-modal-attach-input"
            ref={setFileInput}
            type="file"
            accept="image/*"
            multiple
            style={{ display: 'none' }}
            disabled={status === 'sending'}
          />
          <Button
            test-id="ps-report-bug-modal-attach-button"
            size="small"
            variant="secondary"
            disabled={status === 'sending'}
            onClick={() => fileInput?.click()}
          >
            <Icon name="upload" slot="prefix" /> {labels.attach}
          </Button>

          {previews.length > 0 && (
            <div ref={attachmentsRegion} tabIndex={-1}>
              <Spacer size="2xsmall" />
              <Stack gutter="2xsmall" alignItems="center">
                {previews.map(({ file, url }, index) => (
                  // Keyed by position as well as name: two files picked from
                  // different folders can share one name, and a duplicate key
                  // makes React reuse the wrong preview.
                  <Stack
                    key={`${index}-${file.name}`}
                    gutter="2xsmall"
                    alignItems="center"
                  >
                    <img
                      src={url}
                      alt={labels.screenshotAlt(file.name)}
                      width={64}
                      height={64}
                      style={{ objectFit: 'cover', borderRadius: 4 }}
                    />
                    <IconButton
                      test-id="ps-report-bug-modal-remove-screenshot"
                      size="small"
                      name="close"
                      label={labels.removeScreenshot(file.name)}
                      onClick={() => removeScreenshot(file)}
                    />
                  </Stack>
                ))}
              </Stack>
            </div>
          )}

          {/* Always mounted: a note that appears together with its text is not
              reliably announced (WCAG 4.1.3). */}
          <div role="status" aria-live="polite">
            {rejected.length > 0 && (
              <>
                <Spacer size="2xsmall" />
                {rejected.map(({ file, reason }) => (
                  <TextBody key={`${file.name}-${reason}`} size="small">
                    {labels.rejected[reason](file.name)}
                  </TextBody>
                ))}
              </>
            )}
            {status === 'failed' && (
              <>
                <Spacer size="2xsmall" />
                <TextBody size="small">{labels.sendFailed}</TextBody>
              </>
            )}
            {status === 'sending' && (
              <>
                <Spacer size="2xsmall" />
                <TextBody size="small">{labels.sending}</TextBody>
              </>
            )}
            {notice && (
              <>
                <Spacer size="2xsmall" />
                <TextBody size="small">{notice}</TextBody>
              </>
            )}
          </div>

          {/* Why Send is unavailable, as VISIBLE text. It cannot be a tooltip
              on the button: a disabled `ps-button` renders a native
              `<button disabled>`, which is out of the tab order, so neither a
              keyboard nor a screen-reader user can ever reach the explanation.
              Same reasoning as the Live Agent Platforms empty-offer callout. */}
          {status === 'editing' && !canSubmit && (
            <>
              <Spacer size="2xsmall" />
              <TextBody size="small">{labels.submitHint}</TextBody>
            </>
          )}

          <Spacer size="small" />
          <Stack justifyContent="end" gutter="small">
            <Button
              test-id="ps-report-bug-modal-cancel-btn"
              size="large"
              variant="secondary"
              disabled={status === 'sending'}
              onClick={resetAndClose}
            >
              {labels.cancel}
            </Button>
            <Button
              test-id="ps-report-bug-modal-submit-btn"
              size="large"
              disabled={!canSubmit}
              onClick={handleSubmit}
            >
              {status === 'sending' ? labels.sending : labels.submit}
            </Button>
          </Stack>
        </div>
      )}
    </Modal>
  );
};
